export type DeviceType = 'desktop' | 'mobile' | 'tablet';

/**
 * Representative viewport widths (px) used to seed MUI's `ssrMatchMedia` during
 * server-side rendering, before the client knows the real `window.innerWidth`.
 *
 * These map onto MUI's default breakpoints (xs=0, sm=600, md=960, lg=1280):
 *   - mobile  -> xs range  (< 600)
 *   - tablet  -> sm range  (600–959)
 *   - desktop -> lg range  (>= 1280)
 */
export const DEVICE_TYPE_SSR_WIDTH: Record<DeviceType, number> = {
  mobile: 412,
  tablet: 820,
  desktop: 1280,
};

/**
 * Best-effort device classification from a User-Agent string so the server can
 * render the correct responsive layout on first paint (e.g. desktop visitors
 * with JS disabled no longer get the mobile view).
 *
 * Intentionally dependency-free and conservative: anything we can't confidently
 * classify as mobile/tablet falls back to `desktop`, which is also the safest
 * default for crawlers and SEO.
 */
export function detectDeviceType(userAgent?: string | null): DeviceType {
  if (!userAgent) {
    return 'desktop';
  }

  // Tablets first — Android tablets omit the "Mobile" token, and iPads identify
  // via "iPad". Treating them separately avoids classifying them as phones.
  const isTablet =
    /iPad/i.test(userAgent) ||
    (/Android/i.test(userAgent) && !/Mobile/i.test(userAgent)) ||
    /Tablet|PlayBook|Silk/i.test(userAgent);

  if (isTablet) {
    return 'tablet';
  }

  const isMobile = /Mobile|iPhone|iPod|Android|BlackBerry|IEMobile|Opera Mini|webOS/i.test(userAgent);

  if (isMobile) {
    return 'mobile';
  }

  return 'desktop';
}

/** Maps a device classification to the SSR seed width consumed by `ssrMatchMedia`. */
export function getSsrViewportWidth(deviceType: DeviceType): number {
  return DEVICE_TYPE_SSR_WIDTH[deviceType];
}
